"use client";

import React, { useState } from "react";
import { ArrowUpRight, Compass, MapPin, Search } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
const cityLocations = [{
  name: "Wakefield",
  to: "/LocalSeoConsultantWakefieldPage",
  region: "Yorkshire"
}, {
  name: "Northampton",
  to: "/LocalSeoConsultantNorthamptonPage",
  region: "East Midlands"
}, {
  name: "Stoke on Trent",
  to: "/LocalSeoConsultantStokeOnTrentPage",
  region: "West Midlands"
}, {
  name: "Derby",
  to: "/LocalSeoConsultantDerbyPage",
  region: "East Midlands"
}, {
  name: "Milton Keynes",
  to: "/LocalSeoConsultantMiltonKeynesPage",
  region: "South East"
}, {
  name: "Peterborough",
  to: "/LocalSeoConsultantPeterboroughPage",
  region: "East of England"
}, {
  name: "Warrington",
  to: "/LocalSeoConsultantWarringtonPage",
  region: "North West"
}, {
  name: "Hull",
  to: "/LocalSeoConsultantHullPage",
  region: "Yorkshire"
}, {
  name: "Wolverhampton",
  to: "/LocalSeoConsultantWolverhamptonPage",
  region: "West Midlands"
}, {
  name: "Sunderland",
  to: "/LocalSeoConsultantSunderlandPage",
  region: "North East"
}, {
  name: "Norwich",
  to: "/LocalSeoConsultantNorwichPage",
  region: "East of England"
}, {
  name: "Doncaster",
  to: "/LocalSeoConsultantDoncasterPage",
  region: "Yorkshire"
}, {
  name: "Preston",
  to: "/LocalSeoConsultantPrestonPage",
  region: "North West"
}, {
  name: "Bolton",
  to: "/LocalSeoConsultantBoltonPage",
  region: "North West"
}, {
  name: "Wigan",
  to: "/LocalSeoConsultantWiganPage",
  region: "North West"
}, {
  name: "Lincoln",
  to: "/LocalSeoConsultantLincolnPage",
  region: "East Midlands"
}, {
  name: "Cheltenham",
  to: "/LocalSeoConsultantCheltenhamPage",
  region: "South West"
}, {
  name: "Telford",
  to: "/LocalSeoConsultantTelfordPage",
  region: "West Midlands"
}, {
  name: "Plymouth",
  to: "/LocalSeoConsultantPlymouthPage",
  region: "South West"
}, {
  name: "Gloucester",
  to: "/LocalSeoConsultantGloucesterPage",
  region: "South West"
}, {
  name: "Carlisle",
  to: "/LocalSeoConsultantCarlislePage",
  region: "North West"
}, {
  name: "Belfast",
  to: "/LocalSeoConsultantBelfastPage",
  region: "Northern Ireland"
}, {
  name: "Ipswich",
  to: "/LocalSeoConsultantIpswichPage",
  region: "East of England"
}, {
  name: "Dundee",
  to: "/LocalSeoConsultantDundeePage",
  region: "Scotland"
}, {
  name: "Swansea",
  to: "/LocalSeoConsultantSwanseaPage",
  region: "Wales"
}, {
  name: "Luton",
  to: "/LocalSeoConsultantLutonPage",
  region: "East of England"
}, {
  name: "Chelmsford",
  to: "/LocalSeoConsultantChelmsfordPage",
  region: "East of England"
}, {
  name: "Colchester",
  to: "/LocalSeoConsultantColchesterPage",
  region: "East of England"
}, {
  name: "Bedford",
  to: "/LocalSeoConsultantBedfordPage",
  region: "East of England"
}, {
  name: "York",
  to: "/LocalSeoConsultantYorkPage",
  region: "Yorkshire"
}];
export default function Locations() {
  const shouldReduceMotion = useReducedMotion();
  const [query, setQuery] = useState("");
  const filteredCities = cityLocations.filter(city => city.name.toLowerCase().includes(query.trim().toLowerCase()));
  return <section id="localcoveragelocations" data-nav="light" className="bg-background py-20 md:py-28 border-t border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-12 md:mb-16">
          <div className="max-w-3xl">
            <Badge variant="outline" className="mb-4 text-xs font-semibold uppercase tracking-wider text-primary border-primary/20 bg-muted/50">
              <Compass className="size-3.5 text-primary mr-1" />
              Regional Search Strategy
            </Badge>

            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-default tracking-tight text-foreground">
              Local SEO coverage across the UK
            </h2>

            <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
              I provide targeted regional SEO coverage across the UK, helping businesses establish strong
              search presence across key towns, cities, and regional growth corridors.
              Every territory campaign is managed directly by me, aligning location-specific landing structures,
              Google Business Profiles, and geographic authority signals to your commercial footprint.
            </p>
          </div>

          <div className="w-full sm:w-80 lg:w-72 shrink-0">
            <div className="relative">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" />
              <input type="text" value={query} onChange={e => setQuery(e.target.value)} placeholder="Filter by town or city..." className="w-full pl-10 pr-4 py-2.5 text-sm bg-card text-foreground rounded-lg border border-border placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-primary/40 transition-[border-color,box-shadow]" aria-label="Filter coverage locations" />
            </div>
          </div>
        </div>

        {filteredCities.length === 0 ? <div className="rounded-xl border border-dashed border-border bg-card/60 p-12 text-center">
            <MapPin className="mx-auto size-8 text-muted-foreground mb-3" />
            <p className="text-foreground font-medium">No locations match your filter</p>
            <p className="text-sm text-muted-foreground mt-1">
              Looking for a city not listed here? I still provide national and bespoke regional SEO campaigns.
            </p>
          </div> : <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3.5 sm:gap-4">
            {filteredCities.map((city, i) => <motion.div key={city.name} data-index={i} initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 12
        }} whileInView={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.1
        }} transition={{
          duration: 0.35,
          delay: Math.min(i * 0.02, 0.3)
        }}>
                <Link to={city.to} className="group flex items-center justify-between p-3.5 sm:p-4 rounded-lg bg-card border border-border hover:border-primary/40 hover:bg-muted/30 shadow-2xs hover:shadow-sm transition-[color,background-color,border-color,box-shadow] duration-200">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <span className="flex size-7 items-center justify-center rounded-md bg-muted text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors shrink-0">
                      <MapPin className="size-3.5" />
                    </span>
                    <div className="truncate">
                      <span className="block text-sm font-medium text-foreground group-hover:text-primary transition-colors truncate">
                        {city.name}
                      </span>
                      <span className="block text-[11px] text-muted-foreground tracking-tight">
                        {city.region}
                      </span>
                    </div>
                  </div>

                  <ArrowUpRight className="size-4 text-muted-foreground/60 group-hover:text-primary group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-[transform,color] shrink-0 ml-2" />
                </Link>
              </motion.div>)}
          </div>}

        <div className="mt-12 pt-8 border-t border-border flex flex-col sm:flex-row sm:items-center justify-between gap-4 text-sm text-muted-foreground">
          <p>
            Operating across England, Scotland, Wales, and Northern Ireland. You collaborate directly with Yassine Essaadi. Explore dedicated campaigns for{" "}
            <Link to="/LocalAndConsumerServicesPage" className="text-primary underline underline-offset-4 hover:no-underline">
              local trades, hospitality and property businesses
            </Link>
            .
          </p>
          <Link to="/ContactPage" className="inline-flex items-center text-primary font-medium hover:underline underline-offset-4 shrink-0">
            Discuss a bespoke regional territory
            <ArrowUpRight className="ml-1 size-3.5" />
          </Link>
        </div>
      </div>
    </section>;
}